import classNames from 'classnames';
import React, { MutableRefObject } from 'react';

import Button from '~components/Button/Button';
import Dialog from '~components/Dialog/Dialog';
import { usePageContext } from '~components/PageDataProvider/PageDataProvider';
import useWindowSize from '~hooks/useWindowSize';
import ErrorIcon from '~images/inline/error.inline.svg';
import InfoIcon from '~images/inline/info.inline.svg';

type Props = {
    inputLabel: string;
    buttonTitle: string;
    buttonTitleMobile: string | null;
    buttonTitleMobileSuperSmall: string | null;
    buttonWaLink: string;
    moreInfo: boolean;
    qaValue: string;
    formControl: {
        hasZipError: boolean;
        hasCountyError: boolean;
        showCountySelect: boolean;
        isDialogOpen: boolean;
        setIsDialogOpen: (isOpen: boolean) => void;
    };
    values: {
        zip: string;
        county: string;
        multiCounty: GeoLocation[] | null;
    };
    handlers: {
        zipInput: (e: React.FormEvent<HTMLInputElement>) => void;
        countyChange?: (e: React.FormEvent<HTMLSelectElement>) => void;
        submit: (e: React.SyntheticEvent) => void;
        keyPress: (e: React.KeyboardEvent) => void;
    };
    refs: { tooltip: null | MutableRefObject<HTMLButtonElement> };
    opts: {
        btnStyle?: 'primary' | 'secondary' | 'reskin' | 'orange' | 'link' | 'rebrandAction';
        dialogPosition?: string;
        forceVerticalLayout?: boolean;
        isHorizontalOnMobile?: boolean;
        isVerticalOnTablet?: boolean;
        setConditionalClasses?: (a: boolean) => string;
        useWhiteText?: boolean;
        useWideLayout?: boolean;
        isArticleRedesign?: boolean;
    };
    customClasses?: {
        zipCodeInput?: string;
    };
    hasFixedWidth?: boolean;
    isGeneric?: boolean;
};

const FormLayout: React.FC<Props> = ({
    inputLabel,
    buttonTitle,
    buttonTitleMobile,
    buttonTitleMobileSuperSmall,
    buttonWaLink,
    moreInfo,
    qaValue,
    formControl: { hasZipError, showCountySelect, isDialogOpen, setIsDialogOpen },
    values: { zip: zipValue },
    handlers: { zipInput: handleZipInput, submit: handleSubmit, keyPress: filterKeyPress },
    refs: { tooltip },
    opts: {
        btnStyle = 'primary',
        dialogPosition,
        forceVerticalLayout = false,
        isHorizontalOnMobile = false,
        isVerticalOnTablet = false,
        setConditionalClasses = (props) => {
            return props ? '' : '';
        },
        useWhiteText = false,
        useWideLayout = false, // index only; plan to delete if design can accommodate
        isArticleRedesign = false,
    },
    customClasses = {},
    hasFixedWidth = false,
    isGeneric = false,
}: Props) => {
    const { isMobile, isTablet } = useWindowSize();
    const pageData = usePageContext();

    const useColsOnMobile = isHorizontalOnMobile;
    const useRows =
        forceVerticalLayout || (isMobile && !useColsOnMobile) || (isTablet && isVerticalOnTablet);
    const showWideZipInput = useWideLayout;

    const isWagpill = pageData.location.pathname.includes('wagpill');

    const ZipMoreInfo = () => (
        <>
            <button
                type="button"
                className="ml-1 icon-info w-4 h-4 bg-no-repeat"
                aria-label="More info about zip code"
                onClick={() => setIsDialogOpen(!isDialogOpen)}
                ref={tooltip}
                data-qa="zip-form-more-info"
                aria-expanded={isDialogOpen}
            >
                <InfoIcon className="text-icon-info" />
            </button>
            <Dialog
                content="Your ZIP Code allows us to filter for plans in your area."
                dataQa="zip-form"
                guid={qaValue}
                isOpen={isDialogOpen}
                onClick={() => setIsDialogOpen(false)}
                parentRef={tooltip as MutableRefObject<HTMLButtonElement>}
                positionClasses={dialogPosition}
            />
        </>
    );

    return (
        <form
            data-qa={`zip-form-${qaValue}`}
            className={classNames(
                'relative md:grid',
                `gap-x-${useWideLayout ? '6' : '2'} gap-y-${useWideLayout ? '4' : '2'} lg:gap-${useWideLayout ? '6' : '2'}`,
                'w-full',
                hasFixedWidth && 'w-max',
                useRows
                    ? `grid-rows-${showCountySelect ? '10' : '6'}`
                    : isGeneric
                        ? `auto-rows-auto grid-cols-${useWideLayout ? '4' : showCountySelect ? '3' : '1'}`
                        : `auto-rows-auto grid-cols-${useWideLayout ? '4' : showCountySelect ? '3' : '2'}`,
                setConditionalClasses(showCountySelect),
                isArticleRedesign && 'md:!block'
            )}
            onSubmit={handleSubmit}
        >
            <div
                className={classNames(
                    'text-left',
                    'auto-rows-min gap-1 grid grid-cols-1 w-full',
                    hasFixedWidth && 'w-40',
                    useRows
                        ? `row-span-${hasZipError ? '4' : '3'}`
                        : isGeneric
                            ? `col-span-${showWideZipInput ? '2' : '1'} md:col-span-${useWideLayout ? '4' : '1'} lg:col-span-${useWideLayout ? '2' : '1'}`
                            : `col-span-${showWideZipInput ? '2' : '1'} md:col-span-4 lg:col-span-${useWideLayout ? '2' : '1'}`
                )}
            >
                <div className="flex items-start justify-start relative w-full">
                    <label
                        htmlFor={`zip-code-${qaValue}`}
                        className={classNames(
                            'flex items-center',
                            `font-poppins text-${useWhiteText ? 'white' : 'black'} text-14 leading-5`
                        )}
                    >
                        {inputLabel}
                    </label>
                    {moreInfo && <ZipMoreInfo />}
                </div>
                <input
                    aria-label="zip code"
                    autoComplete="postal-code"
                    data-qa={`zip-form-${qaValue}-zip-input`}
                    id={`zip-code-${qaValue}`}
                    maxLength={5}
                    name="zip"
                    onChange={handleZipInput}
                    onKeyPress={filterKeyPress}
                    required
                    value={zipValue}
                    className={classNames(
                        'bg-white border rounded placeholder-gray text-black h-12 w-full font-poppins',
                        'duration-500 ease-in-out transition-colors',
                        !hasZipError ? 'border-gray' : 'border-red bg-red-lightest',
                        'p-3 leading-22 m-0 z-10',
                        customClasses?.zipCodeInput
                    )}
                />
                <div
                    aria-live="polite"
                    className={classNames(
                        'duration-500 ease-in-out transition-all',
                        'flex items-center overflow-hidden',
                        {
                            'h-0 lg:h-6': !useRows,
                            'min-h-3': useRows && !showCountySelect,
                            'text-white': useWhiteText,
                            'text-red': !useWhiteText,
                            'h-6': hasZipError,
                            'invisible': !hasZipError
                        },
                        {
                            'data-[active=true]:h-6': true
                        }
                    )}
                    data-active={hasZipError}
                    data-qa="zip-error-message"
                >
                    {hasZipError && (
                        <>
                            <ErrorIcon
                                data-qa={`zip-error-icon-${qaValue}`}
                                className="animate-fade-in w-4 h-4 mr-1"
                            />
                            <div
                                className="animate-fade-in whitespace-nowrap text-sm"
                                data-qa={`zip-error-message-${qaValue}`}
                            >
                                {zipValue === '' ? 'ZIP Code is required' : 'Invalid ZIP Code'}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div
                data-qa="zip-button-container"
                className={classNames(
                    'flex items-center',
                    hasFixedWidth && 'w-40',
                    useRows
                        ? 'row-span-1'
                        : isGeneric
                            ? `col-span-${useWideLayout ? '2' : '1'} md:col-span-${useWideLayout ? '4' : '1'} lg:col-span-${useWideLayout ? '2' : '1'} row-start-2`
                            : `col-span-${useWideLayout ? '2' : '1'} md:col-span-4 lg:col-span-${useWideLayout ? '2' : '1'}`
                )}
            >
                <Button
                    datawalink={buttonWaLink}
                    dataqa={`zip-form-${qaValue}-submit-button`}
                    type="submit"
                    styletype={btnStyle}
                    className={classNames('my-4 md:my-0', (isWagpill || useRows || useWideLayout) && 'w-full')}
                    aria-label="Click to see Medicare plans"
                >
                    <span className="hidden lg:block">{buttonTitle}</span>
                    <span className="block lg:hidden">
                        <span className="hidden ss:block">
                            {buttonTitleMobile ? buttonTitleMobile : buttonTitle}
                        </span>
                        <span className="block ss:hidden">
                            {buttonTitleMobileSuperSmall
                                ? buttonTitleMobileSuperSmall
                                : buttonTitle}
                        </span>
                    </span>
                </Button>
            </div>
        </form>
    );
};

export default FormLayout;
